import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import races from './assets/races.jpg';
import im6 from './assets/im6.jpeg';
import im7 from './assets/im7.jpeg';
import im2 from './assets/im2.jpeg';
import im3 from './assets/im3.jpeg';
import im5 from './assets/im5.jpeg';
import im8 from './assets/im8.jpeg';
import im4 from './assets/im4.jpeg';
import im1 from './assets/im1.jpeg';
import trainingImg from './assets/p2.JPG';
import workImg from './assets/adventure3.jpeg';
import AdventureModal from './AdventureModal';

const MyOtherAdventures = () => {
    const [selectedAdventure, setSelectedAdventure] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ✅ Preload all images once
    useEffect(() => {
        const allImages = [
            races, im6, im7, im2, im3, im5, im8, im4, im1, trainingImg, workImg,
        ];

        allImages.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    const styles = {
        section: {
            width: '100%',
            textAlign: 'center',
            color: '#8CCDEB',
            fontFamily: "'Libre Baskerville', serif",
            marginTop: isMobile ? '0px' : '30px', // smaller top margin on phone
            marginBottom: isMobile ? '80px' : '220px', // smaller bottom margin
            position: 'relative',
            overflow: 'hidden',
        },
        title: {
            fontSize: isMobile ? '36px' : '46px',
            fontWeight: '700',
            marginBottom: isMobile ? '50px' : '90px',
            color: '#FFF2AF',
        },
        adventuresContainer: {
            display: 'flex',
            justifyContent: 'center',
            gap: isMobile ? '30px' : '60px',
            flexWrap: 'wrap',
            zIndex: 1,
            position: 'relative',
            alignItems: 'flex-start',
        },
        card: {
            textAlign: 'center',
            width: isMobile ? '85vw' : '380px',
            minHeight: isMobile ? 'auto' : '460px', // ensure even heights only on desktop
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        imageWrapper: {
            borderRadius: '25px',
            overflow: 'hidden',
            width: isMobile ? '100%' : '380px',
            height: isMobile ? '300px' : '380px',
            backgroundColor: '#FFF2AF',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
            cursor: 'pointer',
            flexShrink: 0,
        },
        image: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
        },
        cardTitle: {
            fontSize: isMobile ? '22px' : '26px',
            fontWeight: '700',
            color: '#FFF2AF',
            marginTop: '18px',
            marginBottom: '10px',
            maxWidth: '320px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: 1.3,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block',
            textAlign: 'center',
        },
    };

    const adventures = [
        {
            title: 'Qualifying Races',
            image: races,
            description: `Through my years of giving dogsled tours, one of the most common questions I get asked is how do you feel ready to run the Iditarod. Honestly, I’m still figuring that out. I’m definitely a bit scared—okay, a lot scared— but I’m confident in my abilities and know that I can take care of my dogs and myself alone on the trail.
                 
                To ensure that mushers are prepared, the Iditarod board requires that we complete a minimum of 750 miles of qualifying races. These mid-distance races, ranging from about 150 to 500 miles, are essential for qualification and also invaluable training. Even mushers who are already qualified often join these races yearly for better training.

                My own racing experience includes the Knik 200, the Copper Basin 300, and the Kobuk 440, which were easily some of the most changeling and rewarding experiences of my life. I also love how mid distance races bring the mushing community together. Without them, we’d likely be isolated all winter and probably too socially awkward once the Iditarod starts. It’s inspiring to see the different approaches of fellow mushers, from their dogs and equipment to their strategies. That’s the beauty of mushing: there’s no single right way, yet our purpose is always the same— it’s all for the dogs. 

                While these qualifying races are shorter than Iditarod, the preparation process is quite similar—packing drop bags, organizing the sled, and planning a run-rest schedule. The days leading up can be stressful, but having supportive mentors like Mitch Seavey—my boss and a 3 time Iditarod champion— puts things into perspective. I remember telling him I was scared at the start line of the Copper Basin 300, and he simply said, “Why are you nervous? You’re just mushing, just for longer.” I couldn’t argue with that— yep, you’re just mushing.`,
            images: [im6, im7, im8],
        },
        {
            title: 'Day by Day',
            image: trainingImg,
            description: `Traveling by dogsled is exhilarating, so it would seem reasonable to assume that dog mushers spend most of their time, well—mushing. In reality, the majority of our days are dedicated to caring for our dogs, maintaining our equipment and trails, and wasting time talking to one another about the dozens of things we need to get done.

                You might hear a musher mention a “bootie party” or a “fidding competition.” Both refer to some of the most mundane yet time-consuming parts of daily life: sorting dog booties and replacing chewed necklines, broken tugs, sewing broken harnesses, scooping, cutting meat(to name a few), foot care. They may not sound glamorous, but both are vital to training a successful dog team—so we find ways to turn them into fun activities.

                Working with the dogs out on the trail is the reward. Coaching new leaders and seeing the day-to-day growth in each and every team member brings a deep sense of gratification and makes all the hard work worthwhile.`,
            images: [trainingImg, im4, im1],
        },
        {
            title: 'Who, What, When, Why: The Iditarod',
            image: workImg,
            description: `The Iditarod Trail has its roots in Alaska’s gold rush era, dating back to the late 19th century. Once a vital supply route connecting remote mining towns, it has since been reimagined as the Iditarod Trail Sled Dog Race, first run in 1973.

            Over the past 50 years, the race has evolved and changed in many ways, but its essence remains the same: one musher, sixteen dogs, and a thousand grueling miles across some of the most rugged and remote terrain Mother Nature has to offer.

            Along the way, both humans and dogs face challenges that test every ounce of endurance and spirit. Frigid temperatures — sometimes plunging to -60°F — treacherous trails, and long, sleepless nights are constant companions. Many mushers not only endure these conditions but thrive in them, their resilience forged through countless hours of training in the cold and dark.

            Still, no amount of preparation can truly ready the human body for days on end with little to no sleep. For that reason, most mushers would agree that sleep deprivation is among the most difficult aspects of running the Iditarod.

            For our far tougher Alaskan Huskies, however, these challenges are exactly what they were bred for — it’s what they love and live for. But for the average person, it might sound downright miserable. So why, then?

            My personal reason for entering the Iditarod isn’t just to check a box or add another accomplishment to my list. I simply love these dogs and I love mushing. What’s even more special is that these dogs love it even more. Training with these incredible Alaskan Huskies(do some research, they have superpowers), creates a bond that goes far beyond what most people experience with their pets. Ive had house dogs my entire life, but this is something entirely different.

            In the Iditarod, you rely on both yourself and your dogs. There’s an equal level of respect, trust, and understanding. It’s a true team effort, where my contributions are just as crucial as my dogs’ contributions in order to succeed.

            The loyalty that exists between mushers and their dogs is something I haven’t found with many humans. I also love my home state of Alaska — its rich history, its breathtaking land, and its people. The beauty of Alaska is unparalleled, and most of the areas along the Iditarod trail will never been seen by others unless they travel it themselves with their dog teams. I don’t think there’s a better way to discover myself, my dogs, and Alaska’s true essence.`,
            images: [workImg, im2, im3, im5],
        },
    ];

    return (
        <div style={styles.section}>
            <h2 style={styles.title}>My Adventures</h2>

            <div style={styles.adventuresContainer}>
                {adventures.map((item, index) => (
                    <div key={index} style={styles.card}>
                        <motion.div
                            style={styles.imageWrapper}
                            whileHover={{
                                scale: 1.05,
                                boxShadow: '0 12px 25px rgba(0, 0, 0, 0.35)',
                            }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setSelectedAdventure(item)}
                        >
                            <motion.img
                                src={item.image}
                                alt={item.title}
                                style={styles.image}
                                whileHover={{ scale: 1.1 }}
                                transition={{ duration: 0.4 }}
                            />
                        </motion.div>
                        <h3 style={styles.cardTitle}>{item.title}</h3>
                    </div>
                ))}
            </div>

            <AnimatePresence>
                {selectedAdventure && (
                    <AdventureModal
                        adventure={selectedAdventure}
                        onClose={() => setSelectedAdventure(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default MyOtherAdventures;
